import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    AlertTriangle,
    Shield,
    Clock,
    User,
    FileText,
    X,
    ChevronRight,
    ChevronLeft,
    Filter,
    Search,
    RefreshCw,
    CheckCircle,
    XCircle,
    AlertCircle,
    ArrowUpRight,
    Flame,
    ShieldAlert,
    ShieldCheck,
    Layers,
    Activity,
    SlidersHorizontal,
    ExternalLink,
    Zap,
    Copy,
    Check,
    Radio,
    Terminal,
    Eye
} from 'lucide-react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import { backendURL } from '../config';

export default function Incidents() {
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [copiedId, setCopiedId] = useState(null);
    const [filters, setFilters] = useState({
        severity: '',
        status: '',
        category: '',
        search: ''
    });
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [relatedEvents, setRelatedEvents] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchIncidents = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const res = await axios.get(`${backendURL}/api/soc/incidents`, {
                params: {
                    page,
                    limit: 15,
                    ...filters
                },
                ...authConfig
            });
            setIncidents(res.data?.incidents || []);
            setTotalPages(res.data?.pagination?.totalPages || 1);
        } catch (err) {
            console.error('Error fetching incidents:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const res = await axios.get(`${backendURL}/api/soc/incidents/stats`, authConfig);
            if (res.data) setStats(res.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchIncidentDetails = async (id) => {
        setLoadingDetails(true);
        try {
            const token = localStorage.getItem("token");
            const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const [incidentRes, eventsRes] = await Promise.all([
                axios.get(`${backendURL}/api/soc/incidents/${id}`, authConfig).catch(() => ({ data: null })),
                axios.get(`${backendURL}/api/soc/incidents/${id}/events`, authConfig).catch(() => ({ data: { events: [] } }))
            ]);
            if (incidentRes.data) setSelectedIncident(incidentRes.data);
            if (eventsRes.data?.events) setRelatedEvents(eventsRes.data.events);
        } catch (err) {
            console.error('Error fetching incident details:', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const updateStatus = async (status, note = '') => {
        try {
            const token = localStorage.getItem("token");
            const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            await axios.post(`${backendURL}/api/soc/incidents/${selectedIncident._id}/status`, {
                status,
                analyst: 'current_user',
                note
            }, authConfig);
            fetchIncidents();
            fetchIncidentDetails(selectedIncident._id);
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const closeIncident = async (closure_reason, note) => {
        try {
            const token = localStorage.getItem("token");
            const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            await axios.post(`${backendURL}/api/soc/incidents/${selectedIncident._id}/close`, {
                closure_reason,
                analyst: 'current_user',
                note
            }, authConfig);
            setSelectedIncident(null);
            fetchIncidents();
            fetchStats();
        } catch (err) {
            console.error('Error closing incident:', err);
        }
    };

    useEffect(() => {
        fetchIncidents();
    }, [page, filters]);

    useEffect(() => {
        fetchStats();
    }, []);

    const copyToClipboard = (text, e) => {
        e?.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedId(text);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const totalCount = stats?.active_incidents || 0;
    const criticalCount = stats?.by_severity?.critical || 0;
    const highCount = stats?.by_severity?.high || 0;
    const criticalPct = totalCount > 0 ? Math.round((criticalCount / totalCount) * 100) : 0;
    const highPct = totalCount > 0 ? Math.round((highCount / totalCount) * 100) : 0;

    const severityDonutData = [
        { name: 'Critical', value: criticalCount, color: '#f43f5e' },
        { name: 'High', value: highCount, color: '#f59e0b' }
    ];

    const topCategories = useMemo(() => {
        if (!stats?.by_category) return [];
        return Object.entries(stats.by_category)
            .map(([name, count]) => ({
                name,
                count,
                percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0
            }))
            .sort((a, b) => b.count - a.count);
    }, [stats, totalCount]);

    return (
        <div className="space-y-6 animate-slide-in pb-16">
            
            {/* ─── Top Hero Title & Control Bar ─── */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl pointer-events-none" />
                
                <div className="space-y-1.5 relative z-10">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                            <ShieldAlert className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-white tracking-tight">Security Incidents</h1>
                                <span className="text-[10px] font-mono font-bold bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                                    {totalCount.toLocaleString()} Active Cases
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                                Master triage inventory & autonomous threat correlation ledger across Microsoft Defender XDR, ThreatDown EDR & FortiGate
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 relative z-10 flex-wrap">
                    <button
                        onClick={() => { fetchStats(); fetchIncidents(); }}
                        className="px-3 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl text-xs font-semibold text-slate-300 transition-all flex items-center gap-2 cursor-pointer"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-blue-400' : 'text-slate-400'}`} />
                        <span>Refresh Telemetry</span>
                    </button>
                    <button
                        onClick={() => navigate('/cases')}
                        className="px-3.5 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-xl text-xs font-bold text-amber-300 transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        <span>Kanban Board</span>
                    </button>
                    <button
                        onClick={() => navigate('/investigations')}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-blue-500/20 cursor-pointer"
                    >
                        <Layers className="w-3.5 h-3.5" />
                        <span>AI Forensic Ledger</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* ─── 4 Executive KPI Cards ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                
                {/* Total Incidents */}
                <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-blue-500/30 transition-all">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Active Cases</span>
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                            <Activity className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-2xl font-black text-white font-mono">{totalCount.toLocaleString()}</span>
                        <span className="text-[10px] font-semibold text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">
                            100% Ingested
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Live streaming from multi-vendor detection pipeline
                    </p>
                </div>

                {/* Critical Severity */}
                <div className="bg-[#111827] border border-rose-500/20 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-rose-500/40 transition-all bg-gradient-to-br from-[#111827] to-rose-950/10">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-rose-300 uppercase tracking-wider">Critical Threats</span>
                        <div className="w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400">
                            <Flame className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-2xl font-black text-rose-400 font-mono">{criticalCount.toLocaleString()}</span>
                        <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/20">
                            {criticalPct}% of total
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                        C2 callbacks, malware activity & zero-days
                    </p>
                </div>

                {/* High Severity */}
                <div className="bg-[#111827] border border-amber-500/20 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-amber-500/40 transition-all bg-gradient-to-br from-[#111827] to-amber-950/10">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider">High Severity</span>
                        <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400">
                            <AlertTriangle className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-2xl font-black text-amber-300 font-mono">{highCount.toLocaleString()}</span>
                        <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                            {highPct}% of total
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                        Multi-event anomalous host clusters
                    </p>
                </div>

                {/* Automated Triage & Containment */}
                <div className="bg-[#111827] border border-emerald-500/20 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-emerald-500/40 transition-all">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-emerald-300 uppercase tracking-wider">Perimeter Enforcement</span>
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                            <ShieldCheck className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-2xl font-black text-emerald-400 font-mono">100%</span>
                        <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                            Enforced
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                        Automated FortiGate IPS policy active
                    </p>
                </div>
            </div>

            {/* ─── Visual Threat Analytics Dashboard (Severity + Attack Vectors) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* Left: Severity Donut Breakdown */}
                <div className="lg:col-span-4 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                        <span className="text-xs font-bold text-white uppercase tracking-wider">Severity Distribution</span>
                        <span className="text-[10px] font-mono text-slate-400">{totalCount.toLocaleString()} Total</span>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center py-2">
                        <div className="w-full h-44 relative flex items-center justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={severityDonutData}
                                        innerRadius={52}
                                        outerRadius={74}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {severityDonutData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#0B1120', 
                                            border: '1px solid rgba(255,255,255,0.1)', 
                                            borderRadius: '8px', 
                                            fontSize: '12px',
                                            color: '#fff' 
                                        }} 
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-xl font-black text-white font-mono">{totalCount.toLocaleString()}</span>
                                <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Cases</span>
                            </div>
                        </div>

                        {/* Interactive Legend Chips */}
                        <div className="grid grid-cols-2 gap-3 w-full mt-2">
                            <div 
                                onClick={() => setFilters({ ...filters, severity: filters.severity === 'Critical' ? '' : 'Critical' })}
                                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                                    filters.severity === 'Critical'
                                        ? 'bg-rose-500/20 border-rose-500 text-white'
                                        : 'bg-black/30 border-white/[0.04] hover:border-rose-500/30'
                                }`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-rose-500" />
                                    <span className="text-[11px] font-bold text-rose-300">Critical</span>
                                </div>
                                <div className="mt-1 flex items-baseline justify-between">
                                    <span className="text-sm font-bold text-white font-mono">{criticalCount.toLocaleString()}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{criticalPct}%</span>
                                </div>
                            </div>

                            <div 
                                onClick={() => setFilters({ ...filters, severity: filters.severity === 'High' ? '' : 'High' })}
                                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                                    filters.severity === 'High'
                                        ? 'bg-amber-500/20 border-amber-500 text-white'
                                        : 'bg-black/30 border-white/[0.04] hover:border-amber-500/30'
                                }`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                                    <span className="text-[11px] font-bold text-amber-300">High</span>
                                </div>
                                <div className="mt-1 flex items-baseline justify-between">
                                    <span className="text-sm font-bold text-white font-mono">{highCount.toLocaleString()}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{highPct}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Attack Category Breakdown Progress Bars */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                        <span className="text-xs font-bold text-white uppercase tracking-wider">Top Attack Vectors & Categorization</span>
                        <span className="text-[10px] text-slate-400">Click bar to filter</span>
                    </div>

                    <div className="space-y-3 pt-1">
                        {topCategories.map((cat, idx) => (
                            <div 
                                key={idx}
                                onClick={() => setFilters({ ...filters, category: filters.category === cat.name ? '' : cat.name })}
                                className={`p-3 rounded-xl border transition-all cursor-pointer group ${
                                    filters.category === cat.name
                                        ? 'bg-blue-500/15 border-blue-500/50'
                                        : 'bg-black/30 border-white/[0.04] hover:border-white/[0.1]'
                                }`}
                            >
                                <div className="flex items-center justify-between text-xs mb-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-200 group-hover:text-blue-400 transition-colors">
                                            {cat.name}
                                        </span>
                                        {cat.name.toLowerCase().includes('c2') && (
                                            <span className="text-[9px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20 px-1.5 py-0.2 rounded">
                                                C2 BEACON
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 font-mono">
                                        <span className="text-slate-300 font-bold">{cat.count.toLocaleString()} cases</span>
                                        <span className="text-[11px] text-slate-500">({cat.percentage}%)</span>
                                    </div>
                                </div>
                                <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full rounded-full transition-all duration-500 ${
                                            cat.name.toLowerCase().includes('c2') ? 'bg-gradient-to-r from-rose-500 to-amber-500' :
                                            cat.name.toLowerCase().includes('unauthorized') ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                                            'bg-gradient-to-r from-blue-500 to-indigo-500'
                                        }`}
                                        style={{ width: `${Math.max(cat.percentage, 3)}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ─── Filter Toolbar & Quick-Filter Chips ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-4">
                
                {/* Quick Filter Buttons */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
                    <button
                        onClick={() => setFilters({ severity: '', status: '', category: '', search: '' })}
                        className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer whitespace-nowrap ${
                            !filters.severity && !filters.status && !filters.category && !filters.search
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-black/30 text-slate-400 hover:text-white border border-white/[0.06]'
                        }`}
                    >
                        All ({totalCount.toLocaleString()})
                    </button>
                    <button
                        onClick={() => setFilters({ ...filters, severity: filters.severity === 'Critical' ? '' : 'Critical' })}
                        className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                            filters.severity === 'Critical'
                                ? 'bg-rose-600 text-white shadow-md'
                                : 'bg-rose-500/10 text-rose-300 border border-rose-500/20 hover:bg-rose-500/20'
                        }`}
                    >
                        <Flame className="w-3.5 h-3.5 text-rose-400" />
                        <span>Critical {criticalCount > 0 ? `(${criticalCount.toLocaleString()})` : ''}</span>
                    </button>
                    <button
                        onClick={() => setFilters({ ...filters, severity: filters.severity === 'High' ? '' : 'High' })}
                        className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                            filters.severity === 'High'
                                ? 'bg-amber-600 text-white shadow-md'
                                : 'bg-amber-500/10 text-amber-300 border border-amber-500/20 hover:bg-amber-500/20'
                        }`}
                    >
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                        <span>High {highCount > 0 ? `(${highCount.toLocaleString()})` : ''}</span>
                    </button>
                    <button
                        onClick={() => setFilters({ ...filters, category: filters.category === 'C2' ? '' : 'C2' })}
                        className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                            filters.category === 'C2'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/20'
                        }`}
                    >
                        <Radio className="w-3.5 h-3.5 text-indigo-400" />
                        <span>C2 Beaconing {stats?.by_category?.C2 ? `(${stats.by_category.C2.toLocaleString()})` : ''}</span>
                    </button>
                    <button
                        onClick={() => setFilters({ ...filters, category: filters.category === 'Suspicious Activity' ? '' : 'Suspicious Activity' })}
                        className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                            filters.category === 'Suspicious Activity'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-blue-500/10 text-blue-300 border border-blue-500/20 hover:bg-blue-500/20'
                        }`}
                    >
                        <Activity className="w-3.5 h-3.5 text-blue-400" />
                        <span>Suspicious Activity {stats?.by_category?.['Suspicious Activity'] ? `(${stats.by_category['Suspicious Activity'].toLocaleString()})` : ''}</span>
                    </button>
                </div>

                {/* Detailed Search & Dropdowns Grid */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 pt-2 border-t border-white/[0.04]">
                    <div className="md:col-span-6 relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Search by IP (e.g. 10.80.14.69), Incident ID, or attack keywords..."
                            value={filters.search}
                            onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                            className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-slate-100 text-xs placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                        />
                        {filters.search && (
                            <button 
                                onClick={() => setFilters({ ...filters, search: '' })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <div className="md:col-span-3">
                        <select
                            value={filters.severity}
                            onChange={(e) => { setFilters({ ...filters, severity: e.target.value }); setPage(1); }}
                            className="w-full px-3.5 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-slate-200 text-xs focus:outline-none focus:border-blue-500/50 transition-colors"
                        >
                            <option value="">All Severities</option>
                            <option value="Critical">Critical {criticalCount > 0 ? `(${criticalCount.toLocaleString()})` : ''}</option>
                            <option value="High">High {highCount > 0 ? `(${highCount.toLocaleString()})` : ''}</option>
                        </select>
                    </div>

                    <div className="md:col-span-3">
                        <select
                            value={filters.category}
                            onChange={(e) => { setFilters({ ...filters, category: e.target.value }); setPage(1); }}
                            className="w-full px-3.5 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-slate-200 text-xs focus:outline-none focus:border-blue-500/50 transition-colors"
                        >
                            <option value="">All Categories</option>
                            {topCategories.map(c => (
                                <option key={c.name} value={c.name}>
                                    {c.name} ({c.count.toLocaleString()})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* ─── World-Class Security Incidents Table ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-2xl shadow-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-black/40 border-b border-white/[0.06] text-[10.5px] uppercase font-mono text-slate-400">
                            <tr>
                                <th className="py-3.5 px-4">Severity</th>
                                <th className="py-3.5 px-4">Case ID</th>
                                <th className="py-3.5 px-4">Title & Description</th>
                                <th className="py-3.5 px-4">Categories</th>
                                <th className="py-3.5 px-4">Affected Assets</th>
                                <th className="py-3.5 px-4">Status</th>
                                <th className="py-3.5 px-4">Detected Time</th>
                                <th className="py-3.5 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04] text-[12.5px]">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="py-16 text-center text-slate-500 space-y-3">
                                        <RefreshCw className="w-6 h-6 text-blue-400 animate-spin mx-auto" />
                                        <p className="text-xs font-medium">Querying incident ledger across MongoDB...</p>
                                    </td>
                                </tr>
                            ) : incidents.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="py-16 text-center text-slate-500 space-y-2">
                                        <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto" />
                                        <p className="text-sm font-bold text-white">No matching incidents found</p>
                                        <p className="text-xs text-slate-500">Try adjusting your search filters above</p>
                                    </td>
                                </tr>
                            ) : (
                                incidents.map((incident) => {
                                    const isCrit = (incident.severity || '').toLowerCase() === 'critical';
                                    const normStatus = (incident.status || 'open').toLowerCase();
                                    return (
                                        <tr 
                                            key={incident._id} 
                                            onClick={() => fetchIncidentDetails(incident._id)}
                                            className="hover:bg-white/[0.02] transition-colors group cursor-pointer"
                                        >
                                            {/* Severity Badge */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold border uppercase tracking-wider ${
                                                    isCrit 
                                                        ? 'bg-rose-500/15 text-rose-300 border-rose-500/30' 
                                                        : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                                }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${isCrit ? 'bg-rose-400 animate-pulse' : 'bg-amber-400'}`} />
                                                    {incident.severity}
                                                </span>
                                            </td>

                                            {/* Case ID with Copy */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs font-bold text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded">
                                                        {incident.incident_id}
                                                    </span>
                                                    <button
                                                        onClick={(e) => copyToClipboard(incident.incident_id, e)}
                                                        className="text-slate-600 hover:text-slate-300 transition-colors p-1"
                                                        title="Copy Case ID"
                                                    >
                                                        {copiedId === incident.incident_id ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-slate-500" />}
                                                    </button>
                                                </div>
                                            </td>

                                            {/* Title & Description */}
                                            <td className="py-4 px-4 max-w-md">
                                                <div className="flex items-center gap-2">
                                                    {incident.source?.includes("Defender") ? (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#00A4EF]/15 border border-[#00A4EF]/30 text-[9px] font-mono font-bold text-[#38BDF8] shrink-0">
                                                            🛡️ Defender XDR
                                                        </span>
                                                    ) : incident.source?.includes("ThreatDown") ? (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#00D26A]/15 border border-[#00D26A]/30 text-[9px] font-mono font-bold text-[#00D26A] shrink-0">
                                                            ⚡ ThreatDown EDR
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-[9px] font-mono font-bold text-indigo-400 shrink-0">
                                                            🔥 FortiGate
                                                        </span>
                                                    )}
                                                    <h4 className="font-bold text-white group-hover:text-blue-400 transition-colors leading-snug truncate">
                                                        {incident.incident_name}
                                                    </h4>
                                                </div>
                                                <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">
                                                    {incident.description || "Multi-event telemetry correlated by QueryTel autonomous triage engine"}
                                                </p>
                                            </td>

                                            {/* Categories */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <span className="text-[9.5px] font-mono uppercase bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded font-semibold border border-blue-500/20">
                                                    {incident.category || "ENDPOINT"}
                                                </span>
                                            </td>

                                            {/* Affected Assets */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {(incident.affected_assets || []).slice(0, 2).map((asset, i) => (
                                                        <span key={i} className="px-2 py-0.5 bg-black/40 border border-white/[0.08] text-slate-300 rounded font-mono text-[11px]">
                                                            {asset}
                                                        </span>
                                                    ))}
                                                    {(incident.affected_assets?.length || 0) > 2 && (
                                                        <span className="px-1.5 py-0.5 bg-white/[0.04] text-slate-400 rounded text-[10px]">
                                                            +{incident.affected_assets.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status Badge */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <span className={`px-2 py-0.5 rounded font-mono text-[10px] font-bold uppercase border ${
                                                    normStatus === 'closed'
                                                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                                        : normStatus.includes('progress') || normStatus === 'analysis'
                                                        ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                                                        : normStatus.includes('escalat') || normStatus === 'response'
                                                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                                        : 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                                                }`}>
                                                    {normStatus === 'new' ? 'OPEN' : incident.status?.toUpperCase() || 'OPEN'}
                                                </span>
                                            </td>

                                            {/* Detected Time */}
                                            <td className="py-4 px-4 whitespace-nowrap text-slate-400 text-xs">
                                                <div className="flex items-center gap-1.5">
                                                    <Clock className="w-3 h-3 text-slate-500" />
                                                    <span>{incident.first_detected ? new Date(incident.first_detected).toLocaleString() : 'Recent'}</span>
                                                </div>
                                            </td>

                                            {/* Actions */}
                                            <td className="py-4 px-4 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => fetchIncidentDetails(incident._id)}
                                                        className="px-2.5 py-1 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-lg text-slate-200 text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <span>View Case</span>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const incId = incident.incident_id || incident._id;
                                                            navigate(`/investigations/${encodeURIComponent(incId)}`);
                                                        }}
                                                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1 shadow-md cursor-pointer"
                                                        title="Open in AI Forensic Sandbox"
                                                    >
                                                        <Layers className="w-3 h-3" />
                                                        <span>Investigate</span>
                                                        <ArrowUpRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ─── Pagination Toolbar ─── */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-black/40 border-t border-white/[0.06] text-xs">
                    <span className="text-slate-400">
                        Showing page <strong className="text-white font-mono">{page}</strong> of <strong className="text-white font-mono">{totalPages.toLocaleString()}</strong> ({totalCount.toLocaleString()} incidents)
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(p - 1, 1))}
                            disabled={page === 1 || loading}
                            className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-30 border border-white/[0.08] rounded-lg text-slate-300 transition-all flex items-center gap-1 cursor-pointer"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            <span>Previous</span>
                        </button>
                        <div className="flex items-center gap-1">
                            {[Math.max(1, page - 1), page, Math.min(totalPages, page + 1)].filter((v, i, a) => a.indexOf(v) === i).map((pNum) => (
                                <button
                                    key={pNum}
                                    onClick={() => setPage(pNum)}
                                    className={`w-8 h-8 rounded-lg font-mono font-bold transition-all ${
                                        page === pNum 
                                            ? 'bg-blue-600 text-white shadow-md' 
                                            : 'bg-white/[0.02] text-slate-400 hover:text-white border border-white/[0.04]'
                                    }`}
                                >
                                    {pNum}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                            disabled={page >= totalPages || loading}
                            className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-30 border border-white/[0.08] rounded-lg text-slate-300 transition-all flex items-center gap-1 cursor-pointer"
                        >
                            <span>Next</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── Modern Slide-in Forensic Detail Modal ─── */}
            {selectedIncident && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-6">
                        
                        {/* Header */}
                        <div className="sticky top-0 bg-[#111827] border-b border-white/[0.06] p-6 flex items-center justify-between z-10">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm font-bold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded">
                                        {selectedIncident.incident_id}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                        (selectedIncident.severity || '').toLowerCase() === 'critical'
                                            ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                                            : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                    }`}>
                                        {selectedIncident.severity}
                                    </span>
                                </div>
                                <h2 className="text-lg font-bold text-white">{selectedIncident.incident_name}</h2>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        const incId = selectedIncident.incident_id || selectedIncident._id;
                                        navigate(`/investigations/${encodeURIComponent(incId)}`);
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20 cursor-pointer"
                                >
                                    <Layers className="w-3.5 h-3.5" />
                                    <span>Open AI Forensic Sandbox</span>
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => setSelectedIncident(null)}
                                    className="p-2 hover:bg-white/[0.06] rounded-xl transition-colors text-slate-400 hover:text-white cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            
                            {/* Summary Card */}
                            <div className="p-4 bg-black/40 border border-white/[0.06] rounded-xl space-y-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">AI Forensic Threat Summary</span>
                                <p className="text-xs text-slate-200 leading-relaxed">
                                    {selectedIncident.description || `Autonomous correlation of ${relatedEvents.length > 0 ? `${relatedEvents.length} distinct event signatures` : 'telemetry events'} targeting ${selectedIncident.affected_assets?.join(', ') || 'network assets'}. Classified as ${selectedIncident.severity || 'high'} priority.`}
                                </p>
                            </div>

                            {/* Categories & Threat Vectors */}
                            <div className="space-y-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Categories & Threat Vectors</span>
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-xs font-mono uppercase bg-blue-500/10 text-blue-300 px-2.5 py-1 rounded-lg font-semibold border border-blue-500/20">
                                        {selectedIncident.category || "ENDPOINT, MALWARE"}
                                    </span>
                                </div>
                            </div>

                            {/* Affected Assets Grid */}
                            <div className="space-y-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Affected Assets & Target IPs</span>
                                <div className="flex flex-wrap gap-2">
                                    {(selectedIncident.affected_assets || []).map((asset, idx) => (
                                        <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-300 font-mono text-xs">
                                            <Radio className="w-3 h-3 text-rose-400" />
                                            <span>{asset}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Related Events Telemetry */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                                        Correlated Log Signatures ({relatedEvents.length})
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-mono">FortiGate Syslog Stream</span>
                                </div>
                                <div className="bg-black/40 border border-white/[0.06] rounded-xl overflow-hidden">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-black/60 border-b border-white/[0.06] text-[10px] uppercase font-mono text-slate-400">
                                            <tr>
                                                <th className="py-2.5 px-3">Event Signature</th>
                                                <th className="py-2.5 px-3">Category</th>
                                                <th className="py-2.5 px-3 text-right">Occurrences</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/[0.04]">
                                            {relatedEvents.length > 0 ? (
                                                relatedEvents.map((event, idx) => (
                                                    <tr key={idx} className="hover:bg-white/[0.02]">
                                                        <td className="py-2.5 px-3 text-slate-200 font-mono">{event.eventName || "FortiOS Telemetry Match"}</td>
                                                        <td className="py-2.5 px-3 text-slate-400">{event.category || "Inbound Probe"}</td>
                                                        <td className="py-2.5 px-3 text-right font-mono text-blue-400 font-bold">{event.occurrenceCount || 1}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} className="py-4 text-center text-slate-500">
                                                        Telemetry signatures matched to active firewall session log buffer.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Status Workflow Action Bar (Matched to Cases Board) */}
                            <div className="pt-4 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-400 font-semibold">Triage Action:</span>
                                    <button
                                        onClick={() => updateStatus('Open', 'Moved to Open status')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                            (selectedIncident.status || '').toLowerCase() === 'open' || (selectedIncident.status || '').toLowerCase() === 'new'
                                                ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                                                : 'bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border-white/[0.08]'
                                        }`}
                                    >
                                        Move to Open
                                    </button>
                                    <button
                                        onClick={() => updateStatus('In Progress', 'Analyst initiated deep triage investigation')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                            (selectedIncident.status || '').toLowerCase().includes('progress') || (selectedIncident.status || '').toLowerCase() === 'analysis'
                                                ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                                                : 'bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border-white/[0.08]'
                                        }`}
                                    >
                                        Move to In Progress
                                    </button>
                                    <button
                                        onClick={() => updateStatus('Escalated', 'Escalated for senior SOC containment action')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                            (selectedIncident.status || '').toLowerCase().includes('escalat') || (selectedIncident.status || '').toLowerCase() === 'response'
                                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                                : 'bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border-white/[0.08]'
                                        }`}
                                    >
                                        Move to Escalated
                                    </button>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => closeIncident('True Positive', 'Incident remediated and closed successfully')}
                                        className="px-3.5 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>Resolve & Close Case</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
